import React, { useState, useEffect, useCallback } from "react";
import { Card, Input, Space, message } from "antd";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { toast } from "react-hot-toast";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../interceptors/api";
import StandardButton from "../components/StandardButton";

const DEFAULT_URL =
  "https://example.com/search?query=hello world&sort=ascending&lang=en";

const QueryParamEditor = () => {
  const [url, setUrl] = useState(DEFAULT_URL);
  const [params, setParams] = useState([]);
  const [baseUrl, setBaseUrl] = useState("");
  const [parsed, setParsed] = useState(false);
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();

  const parseURLFrom = useCallback((value) => {
    try {
      const parsedUrl = new URL(value);
      const entries = Array.from(parsedUrl.searchParams.entries());
      setBaseUrl(`${parsedUrl.origin}${parsedUrl.pathname}`);
      setParams(entries);
      setParsed(true);
      return true;
    } catch {
      return false;
    }
  }, []);

  const parseURL = () => {
    if (!parseURLFrom(url)) {
      message.error(t("error"));
    }
  };

  useEffect(() => {
    const id = searchParams.get("share");
    if (id) {
      api
        .get(`/query-param-editor/share/${id}`)
        .then((res) => {
          const data = res.data;
          if (data.baseUrl && data.params) {
            setBaseUrl(data.baseUrl);
            setParams(data.params);
            setParsed(true);
            const composedUrl = `${data.baseUrl}?${data.params
              .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
              .join("&")}`;
            setUrl(composedUrl);
          }
        })
        .catch(() => {
          toast.error(t("server_error"));
        });
      return;
    }
    parseURLFrom(DEFAULT_URL);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        e.preventDefault();
        parseURL();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [url]);

  const updateParam = (index, key, value) => {
    const updated = [...params];
    updated[index] = [key, value];
    setParams(updated);
  };

  const addParam = () => {
    setParams([...params, ["", ""]]);
  };

  const removeParam = (index) => {
    const updated = [...params];
    updated.splice(index, 1);
    setParams(updated);
  };

  const sortParams = () => {
    const sorted = [...params].sort(([a], [b]) =>
      a.localeCompare(b, undefined, { sensitivity: "base" })
    );
    setParams(sorted);
  };

  const getUpdatedURL = () => {
    const query = params
      .filter(([key]) => key.trim() !== "")
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
      .join("&");
    return query ? `${baseUrl}?${query}` : baseUrl;
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(getUpdatedURL());
      toast.success(t('copy_success'));
    } catch {
      toast.error(t('failed_to_copy'));
    }
  };

  const handleShare = async () => {
    try {
      const res = await api.post(`/query-param-editor/share`, { baseUrl, params });
      const data = res.data;
      if (data.id) {
        const link = `${window.location.origin}/tools/query-param-editor?share=${data.id}`;
        await navigator.clipboard.writeText(link);
        toast.success(t('share_success'));
      } else {
        toast.error(t('share_failed'));
      }
    } catch {
      toast.error(t('share_failed'));
    }
  };

  const hasParams = params.length > 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white p-0 text-gray-800">
      <Header />
      <div className="max-w-5xl mx-auto">
        <Card className="shadow-lg rounded-xl p-4">
          <Input.TextArea
            rows={2}
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder={t('enter_url_placeholder')}
            aria-label={t('enter_url_placeholder')}
          />

          <StandardButton
            onClick={parseURL}
            type="secondary"
            className="w-full"
          >
            {t('parse_url_button')}
          </StandardButton>

          {parsed && (
            <div className="mt-6 space-y-4">
              {hasParams ? (
                params.map(([key, value], idx) => (
                  <Space key={idx} className="w-full">
                    <Input
                      placeholder={t('key_placeholder')}
                      value={key}
                      onChange={(e) => updateParam(idx, e.target.value, value)}
                    />
                    <Input
                      placeholder={t('value_placeholder')}
                      value={value}
                      onChange={(e) => updateParam(idx, key, e.target.value)}
                    />
                    <StandardButton
                      onClick={() => removeParam(idx)}
                      type="secondary"
                      variant="outlined"
                      danger
                    >
                      {t('remove_button')}
                    </StandardButton>
                  </Space>
                ))
              ) : (
                <p className="text-sm text-gray-500 italic">
                  {t('query_param_no_params_hint')}
                </p>
              )}

              <div className="flex flex-wrap gap-3">
                <StandardButton
                  onClick={addParam}
                  type="secondary"
                  variant="outlined"
                >
                  {t('add_parameter_button')}
                </StandardButton>
                <StandardButton
                  onClick={sortParams}
                  type="secondary"
                  variant="outlined"
                  disabled={params.length < 2}
                >
                  {t('query_param_sort_button')}
                </StandardButton>
              </div>

              <div className="mt-4 bg-indigo-50 p-4 rounded border border-indigo-200">
                <strong className="text-indigo-700">{t('updated_url_label')}</strong>
                <p className="break-all mt-2 text-gray-800">{getUpdatedURL()}</p>
                <div className="mt-2 flex gap-3">
                  <StandardButton
                    onClick={handleCopy}
                    type="secondary"
                    variant="outlined"
                    size="small"
                  >
                    {t('copy_button')}
                  </StandardButton>
                  <StandardButton
                    onClick={handleShare}
                    type="secondary"
                    variant="outlined"
                    size="small"
                  >
                    {t('share_button')}
                  </StandardButton>
                </div>
              </div>
            </div>
          )}
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default QueryParamEditor;
